import planJson from './data/mutal-plan.json';
import fixtureJson from './data/snapshot.fixture.json';

/** [x, y] in metres from the drawing's corner; y runs down the page. */
export type Point = [number, number];

export interface PlanSpace {
  id: string;
  code: string | null;
  kind: string;
  printedAreaSqm: number | null;
  points: Point[];
  parentId: string | null;
}

export interface PlanFloor {
  key: string;
  labelAr: string;
  order: number;
  context: { kind: string; points: Point[] }[];
  spaces: PlanSpace[];
}

export interface Plan {
  widthM: number;
  heightM: number;
  floors: PlanFloor[];
}

/**
 * THE PUBLIC CONTRACT. Everything the public site knows about a unit.
 *
 * Madar will publish this from CONFIRMED plan links only. It never carries
 * a rent, a tenant's company name, a lease term or a Madar id; `brand` is the
 * name on the sign, which anyone walking past can read.
 */
export interface SnapshotUnit {
  code: string;
  floor: string;
  kind: string;
  areaSqm: number | null;
  /** 0 = no terrace drawn; null = a terrace is drawn with no printed area. */
  terraceSqm: number | null;
  status: 'available' | 'leased';
  brand?: string;
}

export interface Snapshot {
  /** True while the page runs on snapshot.fixture.json rather than Madar's. */
  fixture: boolean;
  units: SnapshotUnit[];
}

export const plan = planJson as unknown as Plan;

/**
 * Copies only the contract's fields, so a snapshot that ever arrives with
 * more on it (a rent, a tenant) cannot reach the page by being spread.
 */
export function readSnapshot(raw: unknown): Snapshot {
  const body = raw as { fixture?: unknown; units?: unknown };
  if (!Array.isArray(body.units)) throw new Error('snapshot: units is not a list');
  const units = body.units.map((u: Record<string, unknown>): SnapshotUnit => {
    if (typeof u.code !== 'string' || typeof u.floor !== 'string') {
      throw new Error('snapshot: a unit has no code or floor');
    }
    const status = u.status === 'leased' ? 'leased' : 'available';
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const out: SnapshotUnit = {
      code: u.code,
      floor: u.floor,
      kind: typeof u.kind === 'string' ? u.kind : 'other',
      areaSqm: num(u.areaSqm),
      terraceSqm: num(u.terraceSqm),
      status,
    };
    if (typeof u.brand === 'string' && u.brand.trim()) out.brand = u.brand.trim();
    return out;
  });
  return { fixture: body.fixture === true, units };
}

export const snapshot = readSnapshot(fixtureJson);
